/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgfTooltipDirective } from '@ng-falcon/ngf-tooltip/ngf-tooltip.directive';
import { Observable, Subscription, combineLatest, debounceTime, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs';
import {
  TESTER_NAVIGATION_SIDEBAR_SERVICE,
  TesterNavigationSidebarService,
  TesterNavigationSidebarUrlKey,
} from '../tester-navigation-sidebar.service';
import { TesterNavigationSidebarExpansionService } from '../tester-navigation-sidebar-expansion.service';
import { TesterNavigationSidebarMenuItemChildComponent } from '../tester-navigation-sidebar-menu-item-child/tester-navigation-sidebar-menu-item-child.component';

let nextUid = 0;

@Component({
  selector: 'ngf-tester-navigation-sidebar-menu-item',
  standalone: true,
  imports: [MatIconModule, NgfTooltipDirective],
  templateUrl: './tester-navigation-sidebar-menu-item.component.html',
  styleUrls: ['./tester-navigation-sidebar-menu-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TesterNavigationSidebarMenuItemComponent implements OnInit, AfterContentInit, OnDestroy {
  @Input() icon: string | null = null;
  @Input() svgIcon: string | null = null;
  @Input() hasChildren = false;
  @Input() href: string | null = null;
  @Input() activeKey: TesterNavigationSidebarUrlKey | null = null;
  /** Section title shown above the children in the compact-mode flyout. */
  @Input() label: string | null = null;

  @HostBinding('class.active') active = false;
  @HostBinding('class.expanded') get isExpanded(): boolean { return this.expanded; }

  /**
   * FLO-54052/FLO-54098: absolute (cross-app) links must carry a target so the host app's AngularJS
   * `html5Mode` link rewriting leaves them alone — the uTest app now lives on the same origin under
   * `/utest/`, so without this the click is swallowed by the tester-app router and nothing happens.
   */
  get externalHrefTarget(): string | null {
    return this.href && /^https?:\/\//.test(this.href) ? '_self' : null;
  }

  @ContentChildren(TesterNavigationSidebarMenuItemChildComponent)
  contentChildren!: QueryList<TesterNavigationSidebarMenuItemChildComponent>;

  @ViewChild('menuLink', { read: ElementRef }) menuLink?: ElementRef<HTMLElement>;
  @ViewChild('flyout', { read: ElementRef }) flyout?: ElementRef<HTMLElement>;

  expanded = false;

  private readonly uid = ++nextUid;
  readonly flyoutId = `ngf-sidebar-flyout-${this.uid}`;
  readonly flyoutTitleId = `ngf-sidebar-flyout-title-${this.uid}`;

  private readonly id = Symbol();
  private readonly compactQuery = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(min-width: 769px) and (max-width: 1024px)')
    : null;
  /** Read by the template for matTooltipDisabled — only show tooltips when
   *  the row is icon-only (≤1024px), since the label is otherwise visible. */
  compact = this.compactQuery?.matches ?? false;
  private readonly compactListener = (e: MediaQueryListEvent): void => this.onCompactChange(e.matches);
  private expansionSub = Subscription.EMPTY;
  private toggleSub = Subscription.EMPTY;
  private closeAllSub = Subscription.EMPTY;
  private activeSub = Subscription.EMPTY;

  constructor(
    @Inject(TESTER_NAVIGATION_SIDEBAR_SERVICE) private sidebarService: TesterNavigationSidebarService,
    @Optional() private expansionService: TesterNavigationSidebarExpansionService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.expansionService) {
      if (this.hasChildren) {
        this.expansionService.registerHasChildren(this.id);
      }
      this.expansionSub = this.expansionService.itemOpened$.subscribe(id => {
        if (id !== this.id && this.expanded) {
          this.setExpanded(false);
        }
      });
      // The bottom toggle button broadcasts via toggleActive$ with either a
      // target id (open me) or null (close any open flyout). The service
      // picks the target so apps where no hasChildren row is active for the
      // current route still get a useful default.
      this.toggleSub = this.expansionService.toggleActive$.subscribe(targetId => {
        if (targetId === null) {
          if (this.expanded) {
            this.setExpanded(false);
          }
          return;
        }
        if (targetId === this.id) {
          this.setExpanded(true);
          this.expansionService.itemOpened$.next(this.id);
        }
      });
      // Sidebar-root click-outside detection — only collapses the compact
      // flyout. In the wide ≥1025px sidebar the children expand inline and
      // shouldn't close just because the user clicks somewhere else on the page.
      this.closeAllSub = this.expansionService.closeAllRequested$.subscribe(() => {
        if (this.expanded && this.compact) {
          this.setExpanded(false);
        }
      });
    }
    this.compactQuery?.addEventListener('change', this.compactListener);
  }

  ngAfterContentInit(): void {
    this.activeSub = this.contentChildren.changes.pipe(
      startWith(this.contentChildren),
      switchMap(() => this.aggregateActive$()),
    ).subscribe(active => {
      const wasActive = this.active;
      this.active = active;

      if (this.hasChildren) {
        this.expansionService?.reportActive(this.id, active);
      }

      // In compact mode the expanded state controls a click-driven flyout —
      // route changes must not auto-open it, otherwise the flyout would pop
      // back open after every selection. Icon highlight (active) still updates.
      if (active && !wasActive && !this.compact) {
        this.setExpanded(true);
        this.expansionService?.itemOpened$.next(this.id);
      } else if (!active && wasActive) {
        this.setExpanded(false);
      }

      this.cdr.markForCheck();
    });
  }

  toggle(event: Event, focusFirstChild = false): void {
    event.stopPropagation();
    const willExpand = !this.expanded;
    this.setExpanded(willExpand);
    if (this.expanded) {
      this.expansionService?.itemOpened$.next(this.id);
      if (focusFirstChild && this.compact) {
        this.focusFirstFlyoutItem();
      }
    }
  }

  handleLinkClick(event: MouseEvent): void {
    if (!this.href && this.hasChildren) {
      this.toggle(event);
    }
  }

  /** Keyboard activation for parent menu-items (no href). The native <a>
   *  doesn't fire click on Enter/Space when href is absent. Space is suppressed
   *  to avoid the default page-scroll. Escape closes an open compact flyout
   *  without page-level effects. */
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.expanded && this.compact) {
      event.preventDefault();
      this.setExpanded(false);
      return;
    }
    if (this.href || !this.hasChildren) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle(event, /* focusFirstChild */ true);
    }
  }

  /** Escape from inside the flyout closes it and returns focus to the row. */
  handleFlyoutKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.expanded && this.compact) {
      event.preventDefault();
      event.stopPropagation();
      this.setExpanded(false);
      this.menuLink?.nativeElement.focus();
    }
  }

  /** Close the compact-mode flyout only when a child link was actually
   *  clicked — clicks on padding, the title, or any other non-link content
   *  inside the panel should leave it open. */
  handleChildrenClick(event: MouseEvent): void {
    if (!this.compact || !this.expanded) {
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest('a')) {
      this.setExpanded(false);
    }
  }

  ngOnDestroy(): void {
    this.expansionSub.unsubscribe();
    this.toggleSub.unsubscribe();
    this.closeAllSub.unsubscribe();
    this.activeSub.unsubscribe();
    this.compactQuery?.removeEventListener('change', this.compactListener);
    if (this.expanded) {
      this.expansionService?.reportExpanded(this.id, false);
    }
    if (this.hasChildren) {
      this.expansionService?.unregisterHasChildren(this.id);
    }
  }

  private onCompactChange(compact: boolean): void {
    this.compact = compact;
    // Leaving compact mode: re-sync expanded with the active route so the
    // inline expansion reflects current navigation. Entering compact mode:
    // close any open flyout to avoid showing a stale one on resize.
    this.setExpanded(compact ? false : this.active);
  }

  /** Single setter so every expanded mutation also reports to the service —
   *  the bottom toggle button's icon depends on whether any flyout is open. */
  private setExpanded(expanded: boolean): void {
    if (this.expanded === expanded) {
      this.cdr.markForCheck();
      return;
    }
    this.expanded = expanded;
    this.expansionService?.reportExpanded(this.id, expanded);
    this.cdr.markForCheck();
  }

  /** After Angular renders the now-visible flyout, push focus to its first
   *  link so keyboard users land inside the just-opened panel instead of
   *  remaining on the parent row. setTimeout(0) defers past the current CD
   *  cycle so the display:none has been cleared. */
  private focusFirstFlyoutItem(): void {
    setTimeout(() => {
      const link = this.flyout?.nativeElement.querySelector<HTMLElement>('a');
      link?.focus();
    });
  }

  private aggregateActive$(): Observable<boolean> {
    const streams: Observable<boolean>[] = [];
    if (this.activeKey) {
      streams.push(this.sidebarService.isActive$(this.activeKey));
    }
    for (const child of this.contentChildren ?? []) {
      streams.push(child.isActive$());
    }
    if (streams.length === 0) {
      return of(false);
    }
    // debounceTime(0) collapses the synchronous cascade that happens when a
    // single route change fans out to each child's isActive$. Without it, the
    // parent flips active → false → active as children emit one-by-one and the
    // icon color flickers. distinctUntilChanged skips redundant emissions
    // when the aggregate boolean doesn't actually change.
    return combineLatest(streams).pipe(
      debounceTime(0),
      map(values => values.some(Boolean)),
      distinctUntilChanged(),
    );
  }
}
